/**
 * A single DNS label: the only shape that can be spliced into a hostname
 * safely. The provisioning RPCs already enforce this (20260416000001,
 * 20260612000002), but `tenants.slug` carries only a UNIQUE constraint — no
 * CHECK — so any other write path (hand-run SQL, a future admin surface) can
 * store a value that is not a label. This is the sink, so it validates here:
 * the return value is assigned to window.location.href and rendered as an
 * href, and a slug like `evil.com#@attacker` splices into a URL whose origin
 * is `https://evil.com`, i.e. an off-site redirect from a trusted control.
 */
const HOSTNAME_LABEL = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

/**
 * Build the correct login URL for a tenant slug.
 * e.g. slug "acme" + current host "wrong.lms.example.com" → "https://acme.lms.example.com/login"
 * Returns null on localhost (subdomain switching doesn't apply) and for any
 * slug that isn't a plain hostname label.
 *
 * Shared by ProtectedRoute (cross-tenant redirect after an authenticated
 * mismatch) and the Login page (the "your account lives on another site"
 * notice after a cross-tenant magic link or password sign-in).
 */
export function buildTenantLoginUrl(slug: string): string | null {
  if (!HOSTNAME_LABEL.test(slug)) return null;

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return null;

  const parts = hostname.split('.');
  // Replace the first subdomain with the correct slug
  if (parts.length >= 3) {
    parts[0] = slug;
  } else {
    parts.unshift(slug);
  }
  return `${window.location.protocol}//${parts.join('.')}${window.location.port ? ':' + window.location.port : ''}/login`;
}
